import { useAuth0 } from '@auth0/auth0-react'
import React from 'react'
import { Navigate, useLocation } from 'react-router-dom'

interface StandaloneAuthGuardProps {
  children: React.ReactNode
}

/**
 * Route guard for standalone Auth0 authentication
 * Redirects to /login if not authenticated
 * Similar to SecureRoute but for Auth0 flow
 */
const StandaloneAuthGuard: React.FC<StandaloneAuthGuardProps> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth0()
  const location = useLocation()

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-secondary-medium dark:bg-gray-800">
        <div className="w-full max-w-md p-8">
          <div className="rounded-base border border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 p-8 shadow-lg">
            <div className="mt-8">
              <div className="text-center">
                <div
                  className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-brand-600 dark:border-brand-500 border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
                  role="status"
                >
                  <span className="!absolute !-m-px !h-px !w-px !overflow-hidden !whitespace-nowrap !border-0 !p-0 ![clip:rect(0,0,0,0)]">
                    Loading...
                  </span>
                </div>
                <p className="mt-2 text-sm text-text-body dark:text-gray-400">Authenticating...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    // Redirect to login page with return URL
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  return <>{children}</>
}

export default StandaloneAuthGuard
